/* cspell:ignore nics */
import { z } from 'zod';

import type { NicConfigurationItem } from '@/types/nic-configuration';

import type { VolumeItem } from '../../../capabilities/container-storage/volumeTypes';
import type { ServiceFormItem } from '../../../capabilities/service/types';
import { OptionalVendorDeviceMetaSchema } from '../../../components/vendorDevice';

const requirementSchema = z.object({
  request: z.union([z.number(), z.string()]).optional(),
  limit: z.union([z.number(), z.string()]).optional(),
});

const dnsConfigSchema = z
  .object({
    nameservers: z.array(z.string()).optional(),
    searches: z.array(z.string()).optional(),
    options: z.array(z.object({ name: z.string(), value: z.string() })).optional(),
  })
  .optional();

const gpuSchema = z
  .object({
    vendor: z.string().optional(),
    device: z.string().optional(),
    vendorID: z.string().optional(),
    deviceID: z.string().optional(),
    number: z.union([z.number(), z.string()]).optional(),
    name: z.string().optional(),
    code: z.string().optional(),
    driverVersion: z.string().optional(),
    cudaVersion: z.string().optional(),
    deviceType: z.string().optional(),
    quantityUnit: z.string().optional(),
    card: z.record(z.string(), requirementSchema).optional(),
  })
  .optional();

export const CiSpecSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  runStrategy: z.string().optional(),
  workspace: z.string().optional(),
  namespace: z.string().optional(),
  datacenter: z.string().optional(),
  creator: z.string().optional(),
  system: z
    .object({
      url: z.string().optional(),
      image: z.string().optional(),
      imageID: z.string().optional(),
      name: z.string().optional(),
      architecture: z.string().optional(),
      os: z.string().optional(),
      version: z.string().optional(),
      command: z.array(z.string()).optional(),
      args: z.array(z.string()).optional(),
      env: z.array(z.object({ name: z.string(), value: z.string().optional() })).optional(),
      security: z.record(z.string(), z.unknown()).optional(),
      devices: z.record(z.string(), z.unknown()).optional(),
      livenessProbe: z.record(z.string(), z.unknown()).optional(),
      options: z
        .object({
          containerDeployConfig: z.union([z.string(), z.record(z.string(), z.unknown())]).optional(),
        })
        .optional(),
      registry: z
        .object({
          server: z.string().optional(),
          username: z.string().optional(),
          password: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  systemDiskSize: z.number().optional(),
  systemDiskPricing: OptionalVendorDeviceMetaSchema,
  resource: z.object({
    cpu: z
      .object({
        physicalCards: z.number().optional(),
        physicalCores: z.number().optional(),
        logicalCores: z.number().optional(),
        request: z.union([z.number(), z.string()]).optional(),
        limit: z.union([z.number(), z.string()]).optional(),
        pricing: OptionalVendorDeviceMetaSchema,
      })
      .optional(),
    memory: z.union([z.number(), z.string(), z.object({ request: z.union([z.number(), z.string()]).optional() })]).optional(),
    memoryPricing: OptionalVendorDeviceMetaSchema,
    cpuPricing: OptionalVendorDeviceMetaSchema,
    gpuPricing: OptionalVendorDeviceMetaSchema,
    gpu: gpuSchema,
    network: z.object({
      vpc: z.object({ name: z.string().optional() }).optional(),
      subnet: z.object({ name: z.string().optional() }).optional(),
      securitygroup: z.object({ name: z.string().optional() }).optional(),
      Bandwidth: z.union([z.number(), z.string()]).optional(),
      egressRate: z.union([z.number(), z.string()]).optional(),
      bandwidthPricing: OptionalVendorDeviceMetaSchema,
      dnsConfig: dnsConfigSchema,
      service: z.array(z.custom<ServiceFormItem>()).optional(),
      services: z.array(z.custom<ServiceFormItem>()).optional(),
      nics: z.array(z.custom<NicConfigurationItem>()).optional(),
    }),
  }),
  volumes: z.array(z.custom<VolumeItem>()).optional(),
  optionMeta: z.record(z.string(), z.unknown()).optional(),
});

export type CiSpec = z.infer<typeof CiSpecSchema>;

export type CiApiPayload = Record<string, unknown>;
